/**
 * What a sponsor sees.
 *
 * Rendered entirely outside AppShell. A sponsor has no sidebar, no client
 * switcher and no navigation, because they have exactly one thing to do, and
 * anything else on the page is a way to get lost or to see something that is
 * not theirs.
 *
 * Whitelabeled: the CLIENT's name and brand colour lead. The sponsor's
 * relationship is with IBBA or M&A Source, not with Front Porch, and a page
 * that led with the wrong brand would read as a phishing attempt from someone
 * being asked to hand over their logo files.
 */

import { T } from '../lib/copyEdit.tsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { formatLabel } from '../lib/fileFormat';

/**
 * Why a sponsor's session stopped working.
 *
 * Mirrors what /api/portal returns on a 401. "replaced" is the one that
 * matters: it is what happens when staff reissue a link, and telling the
 * sponsor to check for a newer email is the only useful thing to say.
 */
type DeadReason = 'none' | 'replaced' | 'expired';

/** Thrown so the reason survives the promise chain. A plain rejection loses it,
 *  which is how the page ended up with a single "something went wrong". */
class DeadSession extends Error {
  constructor(readonly reason: DeadReason) {
    super(reason);
  }
}

interface RequestedVariation {
  id: string;
  orientation: string;
  treatment: string;
}

interface Submission {
  /** Without this the page cannot address a file at all: no thumbnail, no
   *  replace, no per-slot anything. Its absence is why a slot could only ever
   *  print a filename. */
  id: string;
  eventVariationId: string | null;
  status: 'pending' | 'approved' | 'fix_requested';
  decisionNote: string | null;
  filename: string;
  mimeType: string;
  /** Which generated copies exist. Names only; the sponsor reads them back
   *  through a route scoped to their own session. */
  renditions: string[];
  /**
   * Where to read this file's thumbnail from, or null when there is none.
   *
   * GIVEN BY THE SERVER RATHER THAN BUILT HERE, because the two pages that
   * render this payload read renditions through different routes: a sponsor
   * through their session, staff previewing the page through the tenant-scoped
   * review route. Constructing the URL on the client would have hard-coded the
   * sponsor's one and shown staff a page of broken images.
   */
  thumbUrl: string | null;
  /** Artwork already on file that we filled in for them, rather than
   *  something they just sent. */
  carriedOver: boolean;
  confirmed: boolean;
}

interface PortalData {
  companyName: string;
  tierTitle: string | null;
  eventName: string;
  client: {
    name: string;
    primaryColor: string;
    secondaryColor: string | null;
    /** Whitelabeled: info@ibba.org for one client, info@masource.org for the
     *  other. Front Porch is never named on this page. */
    replyToEmail: string;
    /** Where to draw the client's logo, or null when they have not uploaded
     *  one. Never the storage key: that is an opaque address and stays on the
     *  server. */
    logoUrl: string | null;
  };
  deadline: { display: string | null; daysUntil: number | null } | null;
  notesForSponsors: string | null;
  specs: { maxFileSizeMb: number; minRasterWidthPx: number };
  requested: RequestedVariation[];
  submissions: Submission[];
  uploadsEnabled: boolean;
}

/**
 * The five states a slot can be in, as the sponsor sees them.
 *
 * FIVE, NOT THREE. The page used to map the three submission statuses and
 * nothing else, so "we are waiting for this" and "we already have this from
 * last year" were indistinguishable: both showed as no file, or as a file the
 * sponsor was told they had sent when they had not.
 *
 * `tone` drives the pill colour. It is never the only signal — every state
 * says what it is in words too, because a colour is not something everybody
 * can read.
 */
type SlotState = {
  pill: string;
  tone: 'ok' | 'wait' | 'fix' | 'none' | 'keep';
  /** The sentence under the filename. Says what happens NEXT, which is the
   *  thing a sponsor actually wants to know. */
  detail: string;
};

function slotState(submission: Submission | null, clientName: string): SlotState {
  if (!submission) {
    return {
      pill: 'Missing',
      tone: 'none',
      detail: 'Nothing sent yet.',
    };
  }

  if (submission.status === 'approved') {
    return {
      pill: 'Approved',
      tone: 'ok',
      detail: `${clientName} has approved this. Nothing more is needed.`,
    };
  }

  if (submission.status === 'fix_requested') {
    return {
      pill: 'Needs changes',
      tone: 'fix',
      detail: 'Send a new file for this version.',
    };
  }

  /* Carried over and not yet spoken for. Told, not asked: the file is already
     against the slot and will be used unless the sponsor replaces it. */
  if (submission.carriedOver && !submission.confirmed) {
    return {
      pill: 'Keeping',
      tone: 'keep',
      detail: "Already on file from a previous event. We'll reuse this unless you replace it.",
    };
  }

  return {
    pill: 'In review',
    tone: 'wait',
    detail: `${clientName} is checking this over.`,
  };
}

export default function Portal() {
  const [data, setData] = useState<PortalData | null>(null);
  /**
   * Why the session stopped working, or null while it still does.
   *
   * WAS A BARE BOOLEAN, which is the bug QA found on 8 September 2026. A
   * sponsor mid-upload had their session revoked under them when staff pressed
   * "Send Invite Again", and the page told them the link "may have been
   * mistyped or cut short by an email client". They had not mistyped anything.
   * The server now says which of the three it was, and the copy follows.
   */
  const [dead, setDead] = useState<DeadReason | null>(null);

  const reload = useCallback(() => {
    void fetch('/api/portal')
      .then(async (r) => {
        if (r.ok) return r.json() as Promise<{ portal: PortalData }>;
        const body = (await r.json().catch(() => ({}))) as { reason?: DeadReason };
        throw new DeadSession(body.reason ?? 'none');
      })
      .then((b) => setData(b.portal))
      .catch((err: unknown) => setDead(err instanceof DeadSession ? err.reason : 'none'));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  /* A session that has lapsed or been revoked lands here rather than on a
     login screen, because there is nothing for a sponsor to log in to. */
  if (dead) return <PortalUnavailable reason={dead} />;
  if (!data) return <div className="app-loading" aria-busy="true" />;

  return <PortalView data={data} onChanged={reload} />;
}

/**
 * The page itself, given its data.
 *
 * SEPARATED FROM THE FETCHING so that staff can render the same page. The
 * "Preview sponsor portal" button on an event needs to show exactly what the
 * sponsor sees, and the only way to guarantee that is for both to be this
 * component — a second implementation would drift, and a preview that drifts is
 * worse than none, because it is the screen somebody checks before sending
 * forty invitations.
 *
 * `readOnly` is what the preview passes. Every control on the page already
 * takes an `enabled` flag, because the Extras list has always rendered
 * submitted files without upload controls, so read-only mode is the existing
 * path rather than a new one.
 */
export function PortalView({
  data,
  readOnly = false,
  onChanged,
}: {
  data: PortalData;
  readOnly?: boolean;
  onChanged: () => void;
}) {
  const urgent = typeof data.deadline?.daysUntil === 'number' && data.deadline.daysUntil <= 7;
  /* Nothing on the page acts when staff are looking at it. Uploading as a
     sponsor from a staff session is not a thing this app should make possible,
     and a preview that could change state is not a preview. */
  const canAct = data.uploadsEnabled && !readOnly;
  const extras = data.submissions.filter((s) => s.eventVariationId === null);

  return (
    <div className="portal" style={{ ['--brand' as string]: data.client.primaryColor }}>
      <header className="portal-head">
        {/* THE CLIENT'S BRAND LEADS, and until now it led as their NAME IN
            TEXT because there was nothing else to show. The logo field existed
            on the row from the first migration and nothing ever wrote to it.
            Falls back to the wordmark for a client who has not uploaded one,
            so nothing regresses. */}
        {data.client.logoUrl ? (
          <img className="portal-logo" src={data.client.logoUrl} alt={data.client.name} />
        ) : (
          <p className="portal-client">{data.client.name}</p>
        )}
        <h1 className="portal-title">{data.eventName}</h1>
        {/* THREE LINES, NOT ONE SENTENCE, and the reason is the data rather
            than taste. A company name has no length limit anybody controls:
            the longest on file runs to two hundred characters, and inside a
            sentence it pushed the one instruction on this page to the end of a
            five-line paragraph. A sponsor should not have to read past their
            own name to find out what is being asked of them.

            Split, the welcome can run as long as it likes and the instruction
            is always in the same place.

            THE TIER IS AN ELEMENT NOW, NOT A FRAGMENT. It used to be glued
            into the sentence carrying its own " · " separator, so that the
            separator did not strand itself for the many sponsors who have no
            tier. As its own line there is nothing to strand: the line is
            simply absent. It appears nowhere else a sponsor can see, which is
            why it is still shown at all. */}
        <p className="portal-lede">
          <T k="portal.greeting" d="Welcome, {company}!" vars={{ company: data.companyName }} />
        </p>

        {/* NO VARIABLES, deliberately. This is the sentence that has to be
            read and acted on, so it is the one that must not be breakable by
            an edit: there is nothing in it to delete by accident and nothing
            whose value could fail to arrive.

            It says "your logo" rather than "high-resolution logo assets": this
            page asks for VECTOR and says so a few inches below, so inviting a
            high-resolution raster here would have the page arguing with
            itself, and the cost of that argument lands as re-upload rounds. */}
        <p className="portal-ask">
          <T
            k="portal.ask"
            d="Please send your logo below so our team can prepare your placement."
          />
        </p>

        {/* Quieter than both, because it is a label rather than something
            being asked of them.

            THE LINE IS EDITABLE, THE VALUE IS NOT. The tier itself is free
            text on the sponsorship, re-negotiated per event, so it belongs to
            the record rather than to the copy layer. What is editable is the
            wording around it, which is why {tier} is required: without it the
            line would announce a sponsorship level and then not say which. */}
        {data.tierTitle && (
          <p className="portal-tier">
            <T k="portal.tier" d="Sponsorship Level: {tier}" vars={{ tier: data.tierTitle }} />
          </p>
        )}
      </header>

      <main className="portal-body">
        {data.deadline?.display && (
          <p className={`portal-deadline${urgent ? ' portal-deadline--urgent' : ''}`}>
            <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            Due {data.deadline.display}
            {typeof data.deadline.daysUntil === 'number' &&
              data.deadline.daysUntil >= 0 &&
              ` · ${data.deadline.daysUntil} day${data.deadline.daysUntil === 1 ? '' : 's'} left`}
          </p>
        )}

        {data.notesForSponsors && (
          <div className="portal-card">
            <h2>A note from {data.client.name}</h2>
            <p>{data.notesForSponsors}</p>
          </div>
        )}

        <div className="portal-card">
          <h2><T k="portal.need.title" d="What We Need" /></h2>
          {data.requested.length === 0 ? (
            <p>
              <T
                k="portal.need.none"
                d="The specific versions have not been set yet. You will hear from {client} before anything is due."
                vars={{ client: data.client.name }}
              />
            </p>
          ) : (
            <ul className="portal-list">
              {data.requested.map((v) => (
                <UploadSlot
                  key={v.id}
                  label={`${v.orientation}`}
                  sub={v.treatment}
                  variationId={v.id}
                  submission={data.submissions.find((s) => s.eventVariationId === v.id) ?? null}
                  clientName={data.client.name}
                  enabled={canAct}
                  onUploaded={onChanged}
                />
              ))}
            </ul>
          )}
          <p className="portal-specs">
            <T
              k="portal.need.specs"
              d="Vector is best (SVG, EPS, AI or PDF), with text converted to outlines. If you only have a raster file, send it at least {minWidth}px wide. Up to {maxSize}MB per file."
              vars={{
                minWidth: data.specs.minRasterWidthPx,
                maxSize: data.specs.maxFileSizeMb,
              }}
            />
          </p>
        </div>

        <div className="portal-card">
          <h2><T k="portal.extras.title" d="Anything Else" /></h2>
          <p className="portal-specs" style={{ marginBottom: 12 }}>
            <T
              k="portal.extras.copy"
              d="Sending something we did not ask for is fine. Add it here and {client} will file it alongside the rest."
              vars={{ client: data.client.name }}
            />
          </p>
          {/* Extras exist because sponsors send what they have, not what was
              requested. Refusing them would mean a support email instead. */}
          <ul className="portal-list">
            {extras.map((s) => (
              /* "Extra" is the system's own word for a file nobody requested.
                 The sponsor sent it deliberately, so on their page it is
                 described the way they would describe it. Same card as a
                 requested version, so a long filename cannot break it. */
              <UploadSlot
                key={s.id}
                label={s.filename}
                sub="also sent"
                variationId={null}
                submission={s}
                clientName={data.client.name}
                enabled={false}
                onUploaded={onChanged}
              />
            ))}
            <UploadSlot
              label="Something else"
              sub="optional"
              variationId={null}
              submission={null}
              clientName={data.client.name}
              enabled={canAct}
              onUploaded={onChanged}
            />
          </ul>
        </div>

        {!data.uploadsEnabled && (
          <p className="portal-soon">
            Uploading opens shortly. {data.client.name} will email you the moment it does, on this
            same link. Nothing is due from you today.
          </p>
        )}

        {/* T6: "reach out before uploading". Dropped from the build, and the
            one thing on this page that prevents a wrong file rather than
            catching one. Deliberately placed BEFORE the forwarding note,
            because it is the more useful of the two.

            LegalFooter is not used here and must not be: it carries the
            "Made with love by the team at Front Porch Solutions"
            attribution, and this page is whitelabeled. */}
        <div className="portal-help">
          <p className="portal-help-title"><T k="portal.help.title" d="Not sure about your file?" /></p>
          <p>
            <T k="portal.help.copy" d="Reach out before uploading and we will help:" />{' '}
            <a href={`mailto:${data.client.replyToEmail}`}>{data.client.replyToEmail}</a>
          </p>
        </div>

        <p className="portal-foot">
          <T
            k="portal.foot"
            d="This page is just for {company}. Feel free to forward the link to whoever holds your artwork."
            vars={{ company: data.companyName }}
          />
        </p>
      </main>
    </div>
  );
}

/**
 * Shown when a link is dead.
 *
 * The reason matters to the person holding the link — "we replaced it" and "it
 * expired" lead to completely different next steps — so the exchange passes one
 * along. None of these confirm whether a token exists, only what happened to
 * the one in hand.
 */
export function PortalUnavailable({ reason: given }: { reason?: DeadReason } = {}) {
  const [params] = useSearchParams();

  /**
   * A reason handed in beats one in the URL.
   *
   * Two routes arrive here. The /s/:token exchange redirects with the reason in
   * the query string, and it has always got this right. A session that stops
   * working mid-visit has no redirect and no query string, so it passes the
   * reason as a prop instead. Before, that second route had nothing and fell
   * through to the "you may have mistyped it" copy.
   */
  const reason = given ?? params.get('reason');
  const message =
    reason === 'replaced'
      ? 'This link was replaced by a newer one. Check your inbox for the most recent email, or ask your contact to send a fresh link. Anything you have already uploaded is safe.'
      : reason === 'expired'
        ? 'This link has expired. Ask your contact to send a new one and it will work straight away.'
        : /* Deliberately no longer leads with "you may have mistyped it". A
             sponsor reaching this page has usually done nothing wrong, and the
             most common cause is a link that was reissued. */
          'This link is not active. It may have been replaced by a newer one, so check your inbox for the most recent email. If there is not one, ask your contact to send a fresh link.';

  return (
    <div className="portal">
      <header className="portal-head">
        <h1 className="portal-title"><T k="portal.expired.title" d="This link is no longer active" /></h1>
      </header>
      <main className="portal-body">
        <div className="portal-card">
          <p>{message}</p>
        </div>
        <p className="portal-foot">
          <T
              k="portal.replace.reassurance"
              d="Nothing you sent previously has been lost. Anything already uploaded is still on file."
            />
        </p>
      </main>
    </div>
  );
}

/**
 * One slot: what is wanted, what has been sent, and a way to send it.
 *
 * A CARD WITH LEVELS, NOT A ROW. This used to be a flex row holding the
 * version name, a status word, a button and, when something went wrong, an
 * error message, all on one horizontal line inside a 640px column. The name
 * wrapped onto three lines to make room and the error pushed the whole thing
 * out of the card. There was nowhere to PUT an error, which is why it broke
 * the layout rather than merely looking cramped.
 *
 * The levels are: a head (version name and a status pill), the reviewer's note
 * if there is one, a body (thumbnail, filename, what happens next, and the
 * actions), and any failure message. Each is its own block, so nothing has to
 * share a line with anything else and long content wraps instead of pushing.
 *
 * The file input is hidden behind a button rather than styled, because a
 * styled <input type="file"> is a well-known accessibility trap — the native
 * control keeps its keyboard behaviour and label association this way.
 *
 * Errors are shown IN THE SLOT, not as a page-level banner. The server refuses
 * files for specific, fixable reasons (wrong type, an SVG carrying a script)
 * and those messages are only useful next to the slot they concern.
 */
function UploadSlot({
  label,
  sub,
  variationId,
  submission,
  clientName,
  enabled,
  onUploaded,
}: {
  label: string;
  sub: string;
  variationId: string | null;
  submission: Submission | null;
  clientName: string;
  enabled: boolean;
  onUploaded: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /* Highlighted while a file is over this slot. Held per slot rather than per
     page, so the sponsor can see WHICH version they are about to drop onto. */
  const [dragging, setDragging] = useState(false);
  /* A file that passed every check but does not look like a logo, held with
     the notes to show, until the sponsor decides. */
  const [checking, setChecking] = useState<{ file: File; notes: string[] } | null>(null);

  async function send(file: File, acknowledge = false) {
    setBusy(true);
    setProblem(null);
    /* Drop any warning held for a PREVIOUS file. Without this, choosing a new
     * file left the old "Does this look right?" panel on screen with a live
     * "Send It Anyway" still holding the file the sponsor had walked away
     * from — so a refusal for the new file appeared beside a button that sent
     * a different one. An acknowledged re-send is the one case where the
     * warning belongs to the file being sent, so it is left alone. */
    if (!acknowledge) setChecking(null);

    const form = new FormData();
    form.append('file', file);
    if (variationId) form.append('variationId', variationId);
    if (acknowledge) form.append('acknowledge', 'true');

    try {
      const res = await fetch('/api/portal/upload', { method: 'POST', body: form });

      if (res.status === 409) {
        /* Nothing is wrong with the file and nothing has been stored. We are
           asking, not refusing — re-sending with acknowledge is all it takes. */
        const body = (await res.json()) as { signals?: string[] };
        setChecking({ file, notes: body.signals ?? [] });
        return;
      }

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        /* A refusal and a warning are mutually exclusive for one slot. Showing
         * both means showing an error about one file next to an offer to send
         * another. */
        setChecking(null);
        setProblem(body.error ?? 'That did not upload. Please try again.');
        return;
      }

      setChecking(null);
      onUploaded();
    } catch {
      setProblem('That did not upload. Check your connection and try again.');
    } finally {
      setBusy(false);
      // Cleared so choosing the same file again still fires a change event.
      if (input.current) input.current.value = '';
    }
  }

  /** Says "yes, reuse the one you have" for carried-over artwork. Turns
   *  silence into an answer, which is the difference between a sponsor who is
   *  happy and a sponsor who has not read the email. */
  async function confirmKeep() {
    if (!submission) return;
    setBusy(true);
    setProblem(null);
    try {
      const res = await fetch(`/api/portal/submissions/${submission.id}/confirm`, {
        method: 'POST',
      });
      if (!res.ok) {
        setProblem('That did not save. Please try again.');
        return;
      }
      onUploaded();
    } catch {
      setProblem('That did not save. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  const state = slotState(submission, clientName);
  /* Approved artwork can still be replaced. A sponsor who spots a mistake
     after approval, or who rebrands mid-cycle, otherwise has to email someone
     and ask them to delete it. */
  const canSend = enabled && !busy;

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (!canSend) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void send(file);
  }

  return (
    <li className="portal-slotrow">
      <div className="portal-slothead">
        <span className="portal-slot">
          {label} <span className="portal-slot-sub">· {sub}</span>
        </span>
        <span className={`portal-pill portal-pill--${state.tone}`}>{state.pill}</span>
      </div>

      {/* The reviewer's note is the whole reason a rejection is actionable, so
          it gets its own line rather than trailing the filename. */}
      {submission?.decisionNote && (
        <p className="portal-slotnote">{submission.decisionNote}</p>
      )}

      {submission ? (
        <div className="portal-slotbody">
          <SlotThumb submission={submission} />
          <div className="portal-slotmain">
            <p className="portal-slotfile">{submission.filename}</p>
            <p className="portal-slotdetail">{state.detail}</p>
          </div>
          {canSend && (
            <div className="portal-slotactions">
              {submission.carriedOver && !submission.confirmed && (
                <button type="button" className="portal-btn portal-btn--primary" onClick={() => void confirmKeep()}>
                  <T k="portal.replace.keep" d="Keep This" />
                </button>
              )}
              <button type="button" className="portal-btn" onClick={() => input.current?.click()}>
                {busy ? 'Sending…' : 'Replace'}
              </button>
            </div>
          )}
        </div>
      ) : (
        /* An empty slot is a DROP TARGET, not a button in a row. Dragging a
           file from a folder is how most people move one, and a dashed area
           says "put it here" in a way a button does not. The button is still
           there inside it: drag and drop is unusable by keyboard, so it can
           never be the only way to do this. */
        canSend ? (
          <div
            className={`portal-slotbody portal-slotdrop${dragging ? ' is-over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <button type="button" className="portal-btn" onClick={() => input.current?.click()}>
              {busy ? 'Sending…' : 'Choose a File'}
            </button>
            <span className="portal-slotdetail">or drag and drop it here</span>
          </div>
        ) : (
          <div className="portal-slotbody">
            <p className="portal-slotdetail">{state.detail}</p>
          </div>
        )
      )}

      {enabled && (
        <input
          ref={input}
          type="file"
          className="portal-file"
          accept=".svg,.eps,.ai,.pdf,.png,.jpg,.jpeg,image/svg+xml,application/pdf,application/postscript,image/png,image/jpeg"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void send(file);
          }}
        />
      )}

      {/* Asked, never enforced. The sponsor knows what they meant to send and
          we do not — a refusal here on a guess means a support email, which is
          the thing this product exists to avoid. */}
      {checking && (
        <div className="portal-check">
          <p className="portal-check-title"><T k="portal.confirm.title" d="Does this look right?" /></p>
          {checking.notes.map((note) => (
            <p key={note} className="portal-check-note">
              {note}
            </p>
          ))}
          <div className="portal-check-actions">
            <button
              type="button"
              className="portal-btn portal-btn--primary"
              disabled={busy}
              onClick={() => {
                setChecking(null);
                setProblem(null);
                input.current?.click();
              }}
            >
              <T k="portal.replace.choose" d="Choose a Different File" /></button>
            <button
              type="button"
              className="portal-btn"
              disabled={busy}
              onClick={() => void send(checking.file, true)}
            >
              {busy ? 'Sending…' : 'Send It Anyway'}
            </button>
          </div>
        </div>
      )}

      {problem && <p className="portal-slotproblem">{problem}</p>}
    </li>
  );
}

/**
 * The artwork on a slot, or a format badge when there is none.
 *
 * A sponsor with four versions on file is looking at four filenames that all
 * begin with their company name, and picking the wrong one to replace is easy.
 * The picture is what makes the slot identifiable.
 *
 * Falls back to the format badge for EPS, which is never rendered, for a
 * render that failed, and for anything uploaded before derivatives existed.
 */
function SlotThumb({ submission }: { submission: Submission }) {
  const [failed, setFailed] = useState(false);

  /* The server says where to read it from; see PortalSubmission.thumbUrl. */
  const src = submission.renditions.includes('thumb') ? submission.thumbUrl : null;

  if (!src || failed) {
    return (
      <span className="portal-thumb portal-thumb--type" aria-hidden="true">
        {formatLabel(submission.mimeType, submission.filename)}
      </span>
    );
  }

  return (
    <span className="portal-thumb" aria-hidden="true">
      <img src={src} alt="" onError={() => setFailed(true)} />
    </span>
  );
}
